/**
 * Automatisches Speichern mit Entprellung — die Browser-Hälfte von Schritt 6
 * (ARCHITEKTUR.md D3, 6.5, `PROTOTYP-PLAN.md` Schritt 6).
 *
 * **Zwei Sekunden Ruhe nach der letzten Änderung, zusätzlich beim Verlassen der Seite.**
 * Die Zahl steht in D3 und ist dort ausdrücklich **gesetzt, nicht gemessen**: sie
 * begrenzt eine Stunde Arbeit auf höchstens 1.800 Schreibvorgänge und liegt damit unter
 * dem Kontingent von 300 je Stunde, das der Server je Sitzung zählt — real weit
 * darunter, weil niemand zwei Sekunden lang ununterbrochen schiebt.
 *
 * Wer das Entprellen hier ändert, ändert das Rate-Limit mit: beide Zahlen gehören
 * zusammen gedacht, sonst sperrt das Limit den eigenen Nutzer aus. In `wizard-flow` ist
 * genau das passiert (ARCHITEKTUR.md 2.9).
 */

export const ENTPRELLUNG_MS = 2000;

export interface Speicherer<T> {
  /** Etwas hat sich geändert. Der letzte Stand gewinnt; die Uhr beginnt neu. */
  aendern: (daten: T) => void;
  /** Sofort schicken, falls etwas aussteht — beim Verlassen der Seite. */
  jetzt: () => void;
  /** Alles vergessen, ohne zu schicken. Für das Aufräumen einer Komponente. */
  abbrechen: () => void;
  /** Steht etwas aus? Nur für Tests und für die Anzeige „noch nicht gespeichert". */
  offen: () => boolean;
}

/**
 * Ein Speicherer um eine Sendefunktion.
 *
 * `amEnde` sagt der Sendefunktion, ob dies der letzte Versuch vor dem Verlassen der
 * Seite ist — dort braucht `fetch` ein `keepalive`, sonst bricht der Browser die
 * Anfrage mit der Seite ab (`src/lib/api.ts`).
 *
 * Fehler werden **nicht** hier behandelt: was eine Kundin liest, entscheidet die
 * Oberfläche (`src/lib/fehlerliste.ts`). Ein abgewiesener Stand bleibt auch nicht
 * liegen — er kommt mit der nächsten Änderung wieder, weil immer die **ganze** Reihe
 * geschickt wird.
 */
export function speicherer<T>(
  senden: (daten: T, amEnde: boolean) => void,
  entprellungMs: number = ENTPRELLUNG_MS,
): Speicherer<T> {
  let offen: { daten: T } | null = null;
  let uhr: ReturnType<typeof setTimeout> | null = null;

  function uhrAus() {
    if (uhr !== null) {
      clearTimeout(uhr);
      uhr = null;
    }
  }

  function schicken(amEnde: boolean) {
    const ausstehend = offen;
    offen = null;
    uhrAus();
    if (!ausstehend) return;
    senden(ausstehend.daten, amEnde);
  }

  return {
    aendern(daten) {
      offen = { daten };
      // Die Uhr beginnt neu, nicht zusätzlich: wer sechzig Figuren umlegt, soll einen
      // Schreibvorgang auslösen und nicht sechzig.
      uhrAus();
      uhr = setTimeout(() => {
        schicken(false);
      }, entprellungMs);
    },
    jetzt() {
      schicken(true);
    },
    abbrechen() {
      offen = null;
      uhrAus();
    },
    offen() {
      return offen !== null;
    },
  };
}

/**
 * Die beiden Ereignisse, bei denen der Stand sofort raus muss.
 *
 * `pagehide` und `visibilitychange` — **nicht** `beforeunload`: auf iOS löst es nicht
 * verlässlich aus, und genau dort wird der Prototyp bedient. Ein Telefon, dessen
 * Bildschirm zugeht oder das in eine andere App wechselt, feuert `visibilitychange`;
 * `pagehide` fängt das Schließen des Tabs und die Rückkehr in den Seitenverlauf.
 *
 * Gibt die Abmeldung zurück, damit die aufrufende Komponente sie beim Aufräumen benutzt.
 */
export function beimVerlassen(jetztSchicken: () => void): () => void {
  const beiSichtbarkeit = () => {
    if (document.visibilityState === "hidden") jetztSchicken();
  };
  window.addEventListener("pagehide", jetztSchicken);
  document.addEventListener("visibilitychange", beiSichtbarkeit);
  return () => {
    window.removeEventListener("pagehide", jetztSchicken);
    document.removeEventListener("visibilitychange", beiSichtbarkeit);
  };
}
